(() => {
  const cfg = window.PITB_EARLY_ACCESS || { enabled: false, codes: [] };
  const ATTR_KEY = 'pitb_early_access';
  const STORAGE_KEY = 'pitb_ea_code';
  const FALLBACK_ERROR = 'That access code is not valid.';

  const normalize = (value) => String(value || '').trim();
  const enabledCodes = () =>
    (cfg.codes || []).map(normalize).filter(Boolean);

  const matchesCode = (value) => {
    const needle = normalize(value).toLowerCase();
    if (!needle) return false;
    return enabledCodes().some((code) => code.toLowerCase() === needle);
  };

  const isEnabled = () => cfg.enabled === true;
  const isRequired = () => isEnabled();

  const storedCode = () => {
    try {
      return normalize(sessionStorage.getItem(STORAGE_KEY));
    } catch (_error) {
      return '';
    }
  };

  const rememberCode = (code) => {
    try {
      if (code) sessionStorage.setItem(STORAGE_KEY, code);
    } catch (_error) {}
  };

  let unlockedCode = matchesCode(cfg.currentCode)
    ? normalize(cfg.currentCode)
    : matchesCode(storedCode())
      ? storedCode()
      : '';
  const isUnlocked = () => !isEnabled() || matchesCode(unlockedCode);

  const root = document.getElementById('PITB-EarlyAccess');
  const form = root && root.querySelector('[data-pitb-ea-form]');
  const input = root && root.querySelector('[data-pitb-ea-input]');
  const errorEl = root && root.querySelector('[data-pitb-ea-error]');
  const submitBtn = root && root.querySelector('[data-pitb-ea-submit]');

  let pendingResolve = null;
  let pendingPromise = null;
  let pendingRedirect = '';
  let lastFocus = null;
  let submitting = false;

  const getPreorderUrl = () => normalize(cfg.preorderUrl) || '/collections/all';

  const gatedPath = (path) => {
    const raw = String(path || '').split('?')[0].split('#')[0];
    const p = raw.length > 1 && raw.endsWith('/') ? raw.slice(0, -1) : raw;
    if (p === '/cart' || p === '/checkout' || p === '/search' || p === '/products') return true;
    if (p.indexOf('/collections') === 0) return true;
    if (p.indexOf('/products/') === 0) return true;
    if (p.indexOf('/checkouts') === 0) return true;
    if (p.indexOf('/cart/') === 0) return true;
    return false;
  };

  const gatedHref = (href) => {
    if (!href) return false;
    const value = String(href).trim();
    if (!value || value.charAt(0) === '#') return false;
    if (/^(javascript:|mailto:|tel:|sms:)/i.test(value)) return false;
    try {
      const url = new URL(value, window.location.origin);
      if (/^(mailto:|tel:|sms:)/i.test(url.protocol)) return false;
      if (url.origin !== window.location.origin) {
        return url.pathname.indexOf('/checkout') === 0 || url.pathname.indexOf('/checkouts') === 0;
      }
      return gatedPath(url.pathname);
    } catch (_error) {
      return gatedPath(value);
    }
  };

  const rememberReturn = (href) => {
    try {
      if (href) sessionStorage.setItem('pitb_ea_return', href);
    } catch (_error) {}
  };

  const consumeReturn = () => {
    try {
      const href = sessionStorage.getItem('pitb_ea_return') || '';
      sessionStorage.removeItem('pitb_ea_return');
      return href;
    } catch (_error) {
      return '';
    }
  };

  const errorMessage = () =>
    normalize(errorEl && errorEl.textContent) || normalize(cfg.errorText) || FALLBACK_ERROR;

  const setError = (on) => {
    const show = Boolean(on);
    if (input) {
      input.classList.toggle('is-invalid', show);
      input.setAttribute('aria-invalid', show ? 'true' : 'false');
    }
    if (!errorEl) return;
    if (show && !normalize(errorEl.textContent)) errorEl.textContent = errorMessage();
    errorEl.classList.toggle('is-visible', show);
    errorEl.style.setProperty('display', show ? 'block' : 'none', 'important');
    errorEl.style.setProperty('visibility', show ? 'visible' : 'hidden', 'important');
  };

  const close = (unlocked) => {
    const stored = unlocked ? consumeReturn() : '';
    const redirect = unlocked && (pendingRedirect || stored) ? pendingRedirect || stored : '';
    pendingRedirect = '';
    if (!root || root.hasAttribute('hidden')) {
      if (pendingResolve) {
        const resolve = pendingResolve;
        pendingResolve = null;
        pendingPromise = null;
        resolve(Boolean(unlocked));
      }
      if (redirect) window.location.assign(redirect);
      return;
    }
    root.setAttribute('hidden', '');
    document.body.classList.remove('pitb-early-access-open');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(lastFocus);
    } else if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    }
    lastFocus = null;
    setError(false);
    if (pendingResolve) {
      const resolve = pendingResolve;
      pendingResolve = null;
      pendingPromise = null;
      resolve(Boolean(unlocked));
    }
    if (redirect) window.location.assign(redirect);
  };

  const open = () => {
    if (!root) return;
    lastFocus = document.activeElement;
    root.removeAttribute('hidden');
    document.body.classList.add('pitb-early-access-open');
    setError(false);
    if (input) {
      input.value = '';
      if (typeof trapFocus === 'function') trapFocus(root, input);
      else input.focus();
    }
  };

  const unlockLocally = (code) => {
    unlockedCode = code;
    cfg.currentCode = code;
    rememberCode(code);
  };

  const persistCode = async (code) => {
    unlockLocally(code);
    const cartUrl = cfg.cartJs || '/cart.js';
    const cartRes = await fetch(cartUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!cartRes.ok) throw new Error('cart');
    const cart = await cartRes.json();
    const attributes = Object.assign({}, cart.attributes || {}, { [ATTR_KEY]: code });
    const updateRes = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    if (!updateRes.ok) throw new Error('update');
  };

  const readCartCode = async () => {
    const cartUrl = cfg.cartJs || '/cart.js';
    const cartRes = await fetch(cartUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!cartRes.ok) throw new Error('cart');
    const cart = await cartRes.json();
    return normalize(cart && cart.attributes && cart.attributes[ATTR_KEY]);
  };

  const homeHref = () => {
    const home = normalize(cfg.homeUrl) || '/';
    return home.endsWith('/') ? `${home}#early-access` : `${home}/#early-access`;
  };

  const onHomePage = () => {
    const path = window.location.pathname.replace(/\/$/, '') || '/';
    const homePath = String(cfg.homeUrl || '/').replace(/\/$/, '') || '/';
    return path === homePath || path === '/';
  };

  const submitCode = async () => {
    if (!input || submitting) return;
    const code = normalize(input.value);
    if (!matchesCode(code)) {
      setError(true);
      input.focus();
      return;
    }
    submitting = true;
    if (submitBtn) submitBtn.disabled = true;
    try {
      try {
        await persistCode(code);
      } catch (_error) {
        // Cart attribute write can fail under cart validation; local unlock still allows browse/ATC.
        unlockLocally(code);
      }
      close(true);
    } catch (_error) {
      setError(true);
    } finally {
      submitting = false;
      if (submitBtn) submitBtn.disabled = false;
    }
  };

  const resolveExistingCode = async () => {
    const candidates = [unlockedCode, cfg.currentCode, storedCode()];
    for (const candidate of candidates) {
      if (matchesCode(candidate)) return normalize(candidate);
    }
    try {
      const cartCode = await readCartCode();
      if (matchesCode(cartCode)) return cartCode;
    } catch (_error) {}
    return '';
  };

  const promptForCode = (redirectUrl) => {
    if (!isEnabled()) return Promise.resolve(true);
    if (redirectUrl) {
      pendingRedirect = redirectUrl;
      rememberReturn(redirectUrl);
    }
    if (onHomePage() && root) {
      if (pendingPromise) return pendingPromise;
      open();
      pendingPromise = new Promise((resolve) => {
        pendingResolve = resolve;
      });
      return pendingPromise;
    }
    window.location.assign(homeHref());
    return Promise.resolve(false);
  };

  const ensureUnlocked = async (redirectUrl) => {
    if (!isEnabled()) return true;
    const existing = await resolveExistingCode();
    if (existing) {
      try {
        await persistCode(existing);
      } catch (_error) {
        if (matchesCode(existing)) {
          unlockedCode = existing;
          rememberCode(existing);
        } else {
          return promptForCode(redirectUrl);
        }
      }
      return true;
    }
    return promptForCode(redirectUrl);
  };

  const openForPreorder = () => {
    const url = getPreorderUrl();
    if (!isEnabled() || isUnlocked()) {
      window.location.assign(url);
      return Promise.resolve(true);
    }
    rememberReturn(url);
    return ensureUnlocked(url);
  };

  const isAddToCartUrl = (value) => {
    try {
      const url = new URL(String(value || ''), window.location.origin);
      return /\/cart\/add(?:\.js)?\/?$/.test(url.pathname);
    } catch (_error) {
      return /\/cart\/add/.test(String(value || ''));
    }
  };

  const isAddToCartForm = (formEl) => {
    if (!formEl || !formEl.getAttribute) return false;
    const action = formEl.getAttribute('action') || '';
    if (isAddToCartUrl(action)) return true;
    if (formEl.querySelector('[name="add"], .pitb-pc__atc, .pitb-pdp__atc, .product-form__submit')) {
      return Boolean(formEl.querySelector('input[name="id"], [name="id"]'));
    }
    return false;
  };

  const blockAddToCart = async () => {
    if (!isEnabled() || isUnlocked()) return false;
    rememberReturn(window.location.href);
    await ensureUnlocked(window.location.href);
    return !isUnlocked();
  };

  const blockGatedNavigation = (href) => {
    if (!isEnabled() || isUnlocked()) return false;
    if (!gatedHref(href)) return false;
    rememberReturn(href);
    ensureUnlocked(href);
    return true;
  };

  if (isEnabled()) document.body.classList.add('pitb-early-access-on');

  if (isEnabled() && matchesCode(storedCode()) && !matchesCode(cfg.currentCode)) {
    persistCode(storedCode()).catch(() => {});
  }

  if (isEnabled() && window.location.hash === '#early-access') {
    if (isUnlocked()) {
      const ret = consumeReturn();
      if (ret) window.location.assign(ret);
    } else {
      open();
    }
  } else if (isEnabled() && gatedPath(window.location.pathname) && !isUnlocked()) {
    rememberReturn(window.location.href);
    window.location.assign(homeHref());
  }

  // Block AJAX /cart/add.js when locked (product-form uses fetch, not a full navigation).
  if (isEnabled() && typeof window.fetch === 'function') {
    const nativeFetch = window.fetch.bind(window);
    window.fetch = async function pitbEaFetch(input, init) {
      const url =
        typeof input === 'string'
          ? input
          : input && typeof input.url === 'string'
            ? input.url
            : '';
      if (isAddToCartUrl(url) && !isUnlocked()) {
        const ok = await ensureUnlocked(window.location.href);
        if (!ok || !isUnlocked()) {
          return new Response(
            JSON.stringify({
              status: 422,
              message: 'Enter a valid early access code to continue.',
              description: 'Enter a valid early access code to continue.',
            }),
            { status: 422, headers: { 'Content-Type': 'application/json' } }
          );
        }
      }
      return nativeFetch(input, init);
    };
  }

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('[data-pitb-ea-nav]');
      if (trigger) {
        event.preventDefault();
        openForPreorder();
        return;
      }

      if (isEnabled() && !isUnlocked()) {
        const atc = event.target.closest(
          'button[name="add"], .pitb-pc__atc, .pitb-pdp__atc, .product-form__submit, [data-pitb-atc]'
        );
        if (atc && !atc.disabled) {
          event.preventDefault();
          event.stopPropagation();
          blockAddToCart();
          return;
        }
      }

      const link = event.target.closest('a[href]');
      if (!link) return;
      // Contact / help links inside the popup must not be intercepted (mailto, etc.).
      if (root && root.contains(link)) return;
      if (blockGatedNavigation(link.href)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const formEl = event.target;
      if (!formEl || !formEl.getAttribute) return;
      if (!isEnabled() || isUnlocked()) return;
      const action = formEl.getAttribute('action') || '';
      const submitter = event.submitter;
      const submitterName = submitter && submitter.getAttribute && submitter.getAttribute('name');
      const submitterAction =
        submitter && submitter.getAttribute ? submitter.getAttribute('formaction') || '' : '';
      const goingCheckout = submitterName === 'checkout' || gatedHref(submitterAction);
      const goingAdd = isAddToCartForm(formEl) || isAddToCartUrl(action) || submitterName === 'add';
      if (!goingCheckout && !goingAdd && !gatedHref(action)) return;
      event.preventDefault();
      event.stopPropagation();
      const next = goingCheckout ? '/checkout' : window.location.href;
      rememberReturn(next);
      ensureUnlocked(next);
    },
    true
  );

  if (root) {
    root.addEventListener('click', (event) => {
      if (event.target === root || event.target.closest('[data-pitb-ea-close]')) {
        event.preventDefault();
        close(false);
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && root && !root.hasAttribute('hidden')) {
        close(false);
      }
    });
  }

  if (submitBtn) {
    submitBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      submitCode();
    });
  }

  if (form) {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      submitCode();
    });
  }

  if (input) {
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      submitCode();
    });
    input.addEventListener('input', () => {
      if (errorEl && errorEl.classList.contains('is-visible')) setError(false);
    });
  }

  window.PITBEarlyAccess = {
    isEnabled,
    isRequired,
    isUnlocked,
    ensureUnlocked,
    openForPreorder,
    blockGatedNavigation,
    blockAddToCart,
    open,
    close: () => close(false),
  };
})();
